import { useState } from 'react'
import { CheckCircle, XCircle } from 'lucide-react'
import { Button } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Process, ApproveProcessRequest } from '../../types/response'
import { approveProcess } from '../../services'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ApprovalActionsProps {
    process: Process
    onSuccess?: () => void
}

type ApproverRole = 'tech_lead' | 'storekeeper'

const APPROVER_ROLES: { value: ApproverRole; label: string }[] = [
    { value: 'tech_lead', label: 'Tech Lead' },
    { value: 'storekeeper', label: 'Storekeeper' },
]

/**
 * ApprovalActions component - Allows users to approve or reject a process.
 * Only visible when process is in 'pending_approval' status.
 */
const ApprovalActions: React.FC<ApprovalActionsProps> = ({
    process,
    onSuccess,
}) => {
    const [selectedRole, setSelectedRole] = useState<ApproverRole | ''>('')
    const [comments, setComments] = useState('')
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')

    // Mutation for approval/rejection
    const { mutate, isPending } = useMutationWithHandler(
        {
            mutationFn: (data: ApproveProcessRequest) =>
                approveProcess(process.id, data),
            onSuccess: () => {
                // Reset form
                setSelectedRole('')
                setComments('')
                setRejectReason('')
                setRejectDialogOpen(false)
                onSuccess?.()
            },
        },
        {
            successMessage: 'Process approval submitted successfully',
            errorMessage: 'Failed to submit approval',
            reactQueryKeys: QUERY_KEYS.WAREHOUSE_PROCESSES,
        }
    )

    // Check if process can be approved (must be in pending_approval status)
    const canApprove = process.status === 'pending_approval'

    // Check which roles have already approved
    // Action values: 'approved', 'approve', 'CONFIRM-PN', 'CONFIRM-PDC' are all approval actions
    const getApprovedRoles = (): Set<string> => {
        const roles = new Set<string>()
        process.approvals?.forEach((approval) => {
            const action = approval.action?.toLowerCase() || ''
            if (
                action === 'approved' ||
                action === 'approve' ||
                action.startsWith('confirm')
            ) {
                roles.add(approval.approver_role)
            }
        })
        return roles
    }

    const approvedRoles = getApprovedRoles()

    // Check if selected role has already approved (not just submitted any action)
    const hasRoleAlreadyApproved = (role: string): boolean => {
        return (
            process.approvals?.some((approval) => {
                if (approval.approver_role !== role) return false
                const action = approval.action?.toLowerCase() || ''
                return (
                    action === 'approved' ||
                    action === 'approve' ||
                    action.startsWith('confirm')
                )
            }) ?? false
        )
    }

    // Get action prefix based on process type (legacy format)
    const getActionPrefix = (): string => {
        switch (process.type) {
            case 'import_purchase':
            case 'import_return':
                return 'PN' // Phiếu nhập
            case 'export_sale':
            case 'export_loan':
            case 'export_delivery':
                return 'PX' // Phiếu xuất
            case 'internal_transfer':
                return 'PDC' // Phiếu điều chuyển
            case 'inventory_check':
                return 'PKK' // Phiếu kiểm kê
            default:
                return 'PN'
        }
    }

    // Handle approve action
    const handleApprove = () => {
        if (!selectedRole) return
        const prefix = getActionPrefix()
        mutate({
            action: `CONFIRM-${prefix}`,
            role: selectedRole,
            comments: comments || undefined,
        })
    }

    // Handle reject action - opens confirmation dialog
    const handleRejectClick = () => {
        if (!selectedRole) return
        setRejectDialogOpen(true)
    }

    // Confirm rejection
    const handleConfirmReject = () => {
        if (!selectedRole) return
        const prefix = getActionPrefix()
        mutate({
            action: `REJECT-${prefix}`,
            role: selectedRole,
            comments: rejectReason || undefined,
        })
    }

    // Don't render if process cannot be approved
    if (!canApprove) {
        return null
    }

    return (
        <>
            <Card className="mt-2" data-tour="approval-actions-card">
                <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                        Approval Actions
                        <Badge variant="secondary">
                            {process.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Role selector */}
                    <div
                        className="space-y-2"
                        data-tour="approval-role-selector"
                    >
                        <Label>Select Your Role</Label>
                        <Select
                            value={selectedRole}
                            onValueChange={(v) =>
                                setSelectedRole(v as ApproverRole)
                            }
                        >
                            <SelectTrigger className="w-full max-w-xs">
                                <SelectValue placeholder="Select role..." />
                            </SelectTrigger>
                            <SelectContent>
                                {APPROVER_ROLES.map((role) => {
                                    const alreadyApproved =
                                        hasRoleAlreadyApproved(role.value)
                                    return (
                                        <SelectItem
                                            key={role.value}
                                            value={role.value}
                                            disabled={alreadyApproved}
                                        >
                                            {role.label}
                                            {alreadyApproved &&
                                                ' (Already submitted)'}
                                        </SelectItem>
                                    )
                                })}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Approval status summary */}
                    <div
                        className="flex gap-4 text-sm"
                        data-tour="approval-status-summary"
                    >
                        <div className="flex items-center gap-1">
                            {approvedRoles.has('tech_lead') ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                                <XCircle className="w-4 h-4 text-gray-300" />
                            )}
                            <span>Tech Lead</span>
                        </div>
                        <div className="flex items-center gap-1">
                            {approvedRoles.has('storekeeper') ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                                <XCircle className="w-4 h-4 text-gray-300" />
                            )}
                            <span>Storekeeper</span>
                        </div>
                    </div>

                    {/* Comments textarea */}
                    <div className="space-y-2" data-tour="approval-comments">
                        <Label>Comments (Optional)</Label>
                        <Textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Add any comments..."
                            rows={2}
                        />
                    </div>

                    {/* Action buttons */}
                    <div
                        className="flex gap-2 pt-2"
                        data-tour="approval-buttons"
                    >
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleApprove}
                            disabled={
                                !selectedRole ||
                                isPending ||
                                hasRoleAlreadyApproved(selectedRole)
                            }
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRejectClick}
                            disabled={
                                !selectedRole ||
                                isPending ||
                                hasRoleAlreadyApproved(selectedRole)
                            }
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Reject confirmation dialog */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Rejection</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to reject this process? The
                            process will be returned to draft status.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-4">
                        <Label>Reason for Rejection</Label>
                        <Textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Enter reason for rejection..."
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outlined"
                            onClick={() => setRejectDialogOpen(false)}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmReject}
                            disabled={isPending}
                        >
                            Confirm Rejection
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ApprovalActions
